import React from 'react';
import { DisplayComponentProps } from '../../index';
import {
	NoPermissionTooltip,
	OUT_OF_STOCK_MESSAGE,
} from '../permissions/NoPermissionTooltip';

// Base configuration interface for different shop contexts
export interface ShopActionConfig {
	type: 'cart' | 'quote';
	contextId: string; // clientId for cart, quoteId for quote

	// Permissions configuration
	permissions: {
		canOrderOutOfStock?: boolean;
		canOrderOverDailyLimit?: boolean;
		canModifyPrices?: boolean;
	};

	// Operations hook data and handlers
	operations: {
		items: Record<number, { quantity: number; price: number; itemId: number }>;
		isLoading?: boolean;
		isUpdating?: boolean;
		handleItemChange: (operation: {
			type: 'add' | 'update' | 'remove';
			itemId: number;
			quantity?: number;
			price?: number;
		}) => Promise<{ success: boolean; error?: string }>;
		handlePriceChange: (
			itemId: number,
			newPrice: number,
		) => Promise<{ success: boolean; error?: string }>;
	};

	// UI component configuration
	ui: {
		quantityComponent: React.ComponentType<any>;
		priceComponent: React.ComponentType<any>;
		addButtonText: string;
		addingText: string;
	};
}

// Extracted item data structure
interface ItemData {
	itemId: number;
	price: number;
	stock: number;
	perOrderLimit?: number;
}

// Calculated constraints for item operations
interface ItemConstraints {
	maxQuantity?: number;
	isDisabled: boolean;
}

// Props for the base component
export interface BaseShopActionsProps extends DisplayComponentProps {
	showQuantity?: boolean;
	showPrice?: boolean;
	config: ShopActionConfig;
}

// Helper function to extract common item data
function extractItemData(data: any): ItemData {
	const itemId = data._id || data.id;
	const itemPrice =
		typeof data.price === 'string' ? parseFloat(data.price) : data.price;
	const stockValue = data.quantityOnHand || 0;
	const stock =
		typeof stockValue === 'string' ? parseInt(stockValue) : stockValue;
	const perOrderLimit = data.perOrderLimit;

	return {
		itemId,
		price: itemPrice || 0,
		stock,
		perOrderLimit,
	};
}

// Helper function to calculate item constraints based on context
function calculateConstraints(
	itemData: ItemData,
	permissions: ShopActionConfig['permissions'],
): ItemConstraints {
	const { stock, perOrderLimit } = itemData;
	const { canOrderOutOfStock, canOrderOverDailyLimit } = permissions;

	// Determine if the item can be ordered
	const isDisabled = !canOrderOutOfStock && stock === 0;

	// Calculate max quantity based on stock and per-order limit
	let maxQuantity = canOrderOutOfStock ? undefined : stock;

	// Apply per-order limit if user doesn't have permission to exceed
	if (!canOrderOverDailyLimit && perOrderLimit && perOrderLimit > 0) {
		if (maxQuantity === undefined) {
			maxQuantity = perOrderLimit;
		} else {
			maxQuantity = Math.min(maxQuantity, perOrderLimit);
		}
	}

	return {
		maxQuantity,
		isDisabled,
	};
}

/**
 * Base Shop Actions Component
 *
 * Unified component that handles both cart and quote shop actions through configuration.
 * Provides consistent behavior while allowing context-specific customization.
 */
export const BaseShopActions = React.memo(
	function BaseShopActions({
		data,
		showQuantity = true,
		showPrice = true,
		config,
	}: BaseShopActionsProps) {
		// Extract common item data
		const itemData = extractItemData(data);
		const { itemId, price } = itemData;

		// Get current item state from operations
		const { items, handleItemChange, handlePriceChange, isUpdating } =
			config.operations;
		const currentItem = items[itemId];

		// Calculate constraints based on permissions and data
		const constraints = calculateConstraints(itemData, config.permissions);

		// Get current state
		const currentQuantity = currentItem?.quantity || 0;
		const currentPrice = currentItem?.price;
		const isInContext = !!(currentItem && currentItem.quantity > 0);

		// Create memoized props for child components to prevent unnecessary re-renders
		const quantityProps = React.useMemo(
			() => ({
				data,
				currentQuantity,
				maxQuantity: constraints.maxQuantity,
				disabled: constraints.isDisabled || isUpdating,
				onItemChange: handleItemChange,
				addButtonText: config.ui.addButtonText,
				addingText: config.ui.addingText,
				// Context-specific props
				...(config.type === 'cart'
					? {
							cartQuantity: currentQuantity,
							clientId: parseInt(config.contextId),
							itemId,
							price: currentPrice !== undefined ? currentPrice : price,
							onCartChange: handleItemChange,
						}
					: {
							quoteItems:
								currentQuantity > 0
									? {
											[itemId]: {
												quantity: currentQuantity,
												price: currentPrice || price,
												itemId,
											},
										}
									: {},
							onQuoteOperation: handleItemChange,
							isUpdating,
						}),
			}),
			[
				data,
				currentQuantity,
				constraints.maxQuantity,
				constraints.isDisabled,
				isUpdating,
				handleItemChange,
				config,
				itemId,
				price,
				currentPrice,
			],
		);

		const priceProps = React.useMemo(
			() => ({
				itemPrice: price,
				currentPrice,
				itemId,
				currentQuantity,
				onPriceSave: handlePriceChange,
				// Quotes allow adding out-of-stock items, so don't disable the price input based on stock
				disabled: config.type === 'quote' ? false : constraints.isDisabled,
				isUpdating,
				size: 'md' as const,
				// Skip validation for quotes - quotes don't need price constraints
				skipValidation: config.type === 'quote',
				// Context-specific props for legacy components
				...(config.type === 'cart'
					? {
							price,
							cartPrice: currentPrice,
							clientId: parseInt(config.contextId),
							cartQuantity: currentQuantity,
							onCartChange: handleItemChange,
						}
					: {}),
			}),
			[
				price,
				currentPrice,
				itemId,
				currentQuantity,
				handlePriceChange,
				constraints.isDisabled,
				isUpdating,
				config,
				handleItemChange,
			],
		);

		// Get UI components from configuration
		const QuantityComponent = config.ui.quantityComponent;
		const PriceComponent = config.ui.priceComponent;

		return (
			<div className="flex items-center gap-1">
				{/* Quantity Component — when stock blocks ordering, explain it
				    (PD-14023). Quote-context price stays enabled by design, so
				    the tooltip wraps only the quantity control. */}
				{showQuantity && (
					<NoPermissionTooltip
						disabled={constraints.isDisabled}
						message={OUT_OF_STOCK_MESSAGE}
						className="inline-block"
					>
						<QuantityComponent {...quantityProps} />
					</NoPermissionTooltip>
				)}

				{/* Price Component */}
				{showPrice && config.permissions.canModifyPrices && (
					<PriceComponent {...priceProps} />
				)}
			</div>
		);
	},
	(prevProps, nextProps) => {
		// Custom memo comparison - only re-render if relevant props changed
		const prevItemId = prevProps.data._id || prevProps.data.id;
		const nextItemId = nextProps.data._id || nextProps.data.id;

		// Always allow re-render if item ID changed
		if (prevItemId !== nextItemId) {
			return false;
		}

		// Allow re-render if show options changed
		if (
			prevProps.showQuantity !== nextProps.showQuantity ||
			prevProps.showPrice !== nextProps.showPrice
		) {
			return false;
		}

		// Allow re-render if item price changed (affects calculations)
		if (prevProps.data.price !== nextProps.data.price) {
			return false;
		}

		// Allow re-render if config changed (different context)
		if (prevProps.config !== nextProps.config) {
			return false;
		}

		// Prevent re-render for all other prop changes
		return true;
	},
);

export default BaseShopActions;
